import React, { useState, useEffect, useCallback } from 'react';
import {
 Box,
 Typography,
 Paper,
 Button,
 TextField,
 Grid,
 Alert,
 CircularProgress,
 IconButton,
 InputAdornment,
 Tooltip,
 LinearProgress,
 Snackbar,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 FormControl,
 InputLabel,
 Select,
 MenuItem,
 Chip
} from '@mui/material';
import {
 Search as SearchIcon,
 CloudUpload as CloudUploadIcon,
 PersonAdd as PersonAddIcon,
 Close as CloseIcon,
 Edit as EditIcon,
 Delete as DeleteIcon,
 VpnKey as VpnKeyIcon,
 PowerSettingsNew as PowerSettingsNewIcon,
 Sync as SyncIcon,
 Download as DownloadIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';


const API_URL = process.env.REACT_APP_API_URL || "http://";


const StudentManagement = () => {
 const [students, setStudents] = useState([]);
 const [loading, setLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [uploadLoading, setUploadLoading] = useState(false);
 const [snackbarOpen, setSnackbarOpen] = useState(false);
 const [snackbarMessage, setSnackbarMessage] = useState('');
 const [snackbarSeverity, setSnackbarSeverity] = useState('success');
 const [addDialogOpen, setAddDialogOpen] = useState(false);
 const [editDialogOpen, setEditDialogOpen] = useState(false);
 const [selectedStudent, setSelectedStudent] = useState(null);
 const [addStudentLoading, setAddStudentLoading] = useState(false);
 const [editStudentLoading, setEditStudentLoading] = useState(false);
 const [paginationModel, setPaginationModel] = useState({
   page: 0,
   pageSize: 10,
 });
 const [totalStudents, setTotalStudents] = useState(0);
 const [newStudent, setNewStudent] = useState({
   prn: '',
   name: '',
   email: '',
   phone: '',
   courseName: ''
 });
 const [errors, setErrors] = useState({});
 const [courses, setCourses] = useState([]);
 const [syncFilter, setSyncFilter] = useState('');
 const [exportLoading, setExportLoading] = useState(false);
 const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
 const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
 const [selectedStudentForReset, setSelectedStudentForReset] = useState(null);
 const [resetPassword, setResetPassword] = useState('');


 const fetchStudents = useCallback(async () => {
   try {
     const params = {
       page: paginationModel.page + 1, // Backend uses 1-based indexing
       limit: paginationModel.pageSize,
       ...(searchTerm && { search: searchTerm }),
       ...(syncFilter && { sync: syncFilter }),
       includeDeleted: 'true'
     };


     const response = await axios.get(`${API_URL}/students`, { params });
     setStudents(response.data.students || []);
     setTotalStudents(response.data.total || 0);
   } catch (error) {
     console.error('Error fetching students:', error);
     setStudents([]);
     setTotalStudents(0);
   } finally {
     setLoading(false);
   }
 }, [paginationModel.page, paginationModel.pageSize, searchTerm, syncFilter]);


 useEffect(() => {
   fetchStudents();
 }, [fetchStudents]);


 useEffect(() => {
   fetchCourses();
 }, []);


 const fetchCourses = async () => {
   try {
     const response = await axios.get(`${API_URL}/courses`);
     setCourses(response.data.courses || []);
   } catch (error) {
     console.error('Error fetching courses:', error);
   }
 };


 const validateForm = () => {
   const newErrors = {};


   if (!newStudent.prn.trim()) {
     newErrors.prn = 'PRN is required';
   }


   if (!newStudent.name.trim()) {
     newErrors.name = 'Name is required';
   }


   if (!newStudent.email.trim()) {
     newErrors.email = 'Email is required';
   } else if (!/\S+@\S+\.\S+/.test(newStudent.email)) {
     newErrors.email = 'Please enter a valid email';
   }


   if (!newStudent.phone.trim()) {
     newErrors.phone = 'Phone is required';
   }


   if (!newStudent.courseName.trim()) {
     newErrors.courseName = 'Course is required';
   }


   setErrors(newErrors);
   return Object.keys(newErrors).length === 0;
 };


 const generateRandomPassword = () => {
   const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
   let password = '';
   for (let i = 0; i < 8; i++) {
     password += chars.charAt(Math.floor(Math.random() * chars.length));
   }
   return password;
 };


 const handleAddStudent = async () => {
   if (!validateForm()) {
     return;
   }
  
   setAddStudentLoading(true);
  
   try {
     await axios.post(`${API_URL}/students`, newStudent);
     showSnackbar('Student added successfully!', 'success');
     setAddDialogOpen(false);
     setNewStudent({
       prn: '',
       name: '',
       email: '',
       phone: '',
       courseName: ''
     });
     setErrors({});
     fetchStudents();
   } catch (error) {
     const errorMessage = error.response?.data?.message || 'Failed to add student';
     showSnackbar(errorMessage, 'error');
   } finally {
     setAddStudentLoading(false);
   }
 };


 const handleCloseAddDialog = () => {
   setAddDialogOpen(false);
   setNewStudent({
     prn: '',
     name: '',
     email: '',
     phone: '',
     courseName: ''
   });
   setErrors({});
 };


 const handleEdit = (student) => {
   setSelectedStudent(student);
   setNewStudent({
     prn: student.prn,
     name: student.name,
     email: student.email,
     phone: student.phone,
     courseName: student.course !== 'N/A' ? student.course : ''
   });
   setEditDialogOpen(true);
 };


 const handleUpdateStudent = async () => {
   if (!validateForm()) {
     return;
   }


   setEditStudentLoading(true);


   try {
     await axios.put(`${API_URL}/students/${selectedStudent.prn}`, newStudent);
     showSnackbar('Student updated successfully!', 'success');
     setEditDialogOpen(false);
     setSelectedStudent(null);
     setNewStudent({
       prn: '',
       name: '',
       email: '',
       phone: '',
       courseName: ''
     });
     setErrors({});
     fetchStudents();
   } catch (error) {
     const errorMessage = error.response?.data?.message || 'Failed to update student';
     showSnackbar(errorMessage, 'error');
   } finally {
     setEditStudentLoading(false);
   }
 };


 const handleDelete = async (prn, isDeleted) => {
   if (window.confirm('Are you sure you want to delete this student?')) {
     try {
        await axios.delete(`${API_URL}/students/${prn}`, { params: { isDeleted } });
        showSnackbar('Student deleted successfully!', 'success');
        fetchStudents();
     } catch (error) {
       showSnackbar('Failed to delete student', 'error');
     }
   }
 };


 const handleRestore = async (prn) => {
   if (window.confirm('Are you sure you want to restore this student?')) {
     try {
       await axios.patch(`${API_URL}/students/${prn}/restore`);
       showSnackbar('Student restored successfully!', 'success');
       fetchStudents();
     } catch (error) {
       showSnackbar('Failed to restore student', 'error');
     }
   }
 };


 const handleResetPassword = (student) => {
   const generatedPassword = generateRandomPassword();
   setResetPassword(generatedPassword);
   setSelectedStudentForReset(student);
   setResetPasswordDialogOpen(true);
 };


 const handleSendResetPassword = async () => {
   if (!resetPassword || resetPassword.length < 6) {
     showSnackbar('Password must be at least 6 characters long', 'error');
     return;
   }


   try {
     await axios.put(`${API_URL}/students/${selectedStudentForReset.prn}/reset-password`, { newPassword: resetPassword, resetBy : 'admin' });
     showSnackbar('Password reset successfully!', 'success');
     setResetPasswordDialogOpen(false);
     setSelectedStudentForReset(null);
     setResetPassword('');
     fetchStudents();
   } catch (error) {
     showSnackbar('Failed to reset password', 'error');
   }
 };


 const handleCloseResetPasswordDialog = () => {
   setResetPasswordDialogOpen(false);
   setSelectedStudentForReset(null);
   setResetPassword('');
 };


 const handleToggleStatus = async (student) => {
   const action = student.isActive ? 'deactivate' : 'activate';
   if (window.confirm(`Are you sure you want to ${action} ${student.name}?`)) {
     try {
       await axios.patch(`${API_URL}/students/${student.prn}/toggle-status`);
       showSnackbar(`Student ${student.isActive ? 'deactivated' : 'activated'} successfully!`, 'success');
       fetchStudents();
     } catch (error) {
       showSnackbar(`Failed to ${action} student`, 'error');
     }
   }
 };


 const handleCloseEditDialog = () => {
   setEditDialogOpen(false);
   setSelectedStudent(null);
   setNewStudent({
     prn: '',
     name: '',
     email: '',
     phone: '',
     courseName: ''
   });
   setErrors({});
 };


 const showSnackbar = (message, severity) => {
   setSnackbarMessage(message);
   setSnackbarSeverity(severity);
   setSnackbarOpen(true);
 };


 const handleFileUpload = async (event) => {
   const file = event.target.files[0];
   if (!file) return;


   // Validate file type - support multiple formats
   const validExtensions = ['.xlsx', '.xlsm', '.ods', '.csv', '.xla', '.numbers', '.xls'];
   const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  
   if (!validExtensions.includes(fileExtension)) {
     showSnackbar('Please select a valid file format (.xlsx, .xlsm, .ods, .csv, .xla, .numbers, .xls)', 'error');
     return;
   }


   // Validate file size (max 10MB)
   if (file.size > 10 * 1024 * 1024) {
     showSnackbar('File size must be less than 10MB', 'error');
     return;
   }


   setUploadLoading(true);
   const formData = new FormData();
   formData.append('file', file);


   try {
     const response = await axios.post(`${API_URL}/students/import`, formData, {
       headers: {
         'Content-Type': 'multipart/form-data'
       }
     });
    
     const { results } = response.data;
     showSnackbar(
       `Import completed! ${results.success} students imported, ${results.failed} failed.`,
       'success'
     );
    
     if (results.errors.length > 0) {
       console.log('Import errors:', results.errors);
     }
    
     fetchStudents();
    
     // Reset file input
     event.target.value = '';
    
   } catch (error) {
     const errorMessage = error.response?.data?.message || 'Failed to import students';
     showSnackbar(errorMessage, 'error');
   } finally {
     setUploadLoading(false);
   }
 };


 const handleSearch = (e) => {
   e.preventDefault();
   setLoading(true);
   // Reset to first page when searching
   setPaginationModel(prev => ({ ...prev, page: 0 }));
   fetchStudents();
 };


 const handlePaginationModelChange = (newModel) => {
   setPaginationModel(newModel);
 };


 const handleBulkSync = async () => {
   if (window.confirm('Are you sure you want to sync all non-synced students with the external API?')) {
     setBulkSyncLoading(true);
     try {
       const response = await axios.post(`${API_URL}/students/bulk-sync`);
       const { results } = response.data;
       showSnackbar(
         `Bulk sync completed! ${results.success} synced, ${results.failed} failed.`,
         'success'
       );
       fetchStudents();
     } catch (error) {
       const errorMessage = error.response?.data?.message || 'Failed to perform bulk sync';
       showSnackbar(errorMessage, 'error');
     } finally {
       setBulkSyncLoading(false);
     }
   }
 };


 const handleExportExcel = async () => {
   try {
     setExportLoading(true);
     showSnackbar('Fetching all students from backend...', 'info');
    
     // Fetch all students from backend without pagination
     const response = await axios.get(`${API_URL}/students`, {
       params: {
         limit: 999999,  // Fetch all records
         includeDeleted: 'true',
         includePasswords: 'true' // Get decrypted passwords for export
       }
     });
    
     const allStudents = response.data.students || [];
    
     if (allStudents.length === 0) {
       showSnackbar('No students to export', 'warning');
       setExportLoading(false);
       return;
     }


     showSnackbar(`Preparing ${allStudents.length} students for export...`, 'info');
    
     // Prepare data as array of arrays for better performance (ordered as requested)
     const headers = [[
       'Sr No',
       'Programme',
       'Name',
       'PRN',
       'Institutional Email',
       'Email',
       'Mobile Number'
     ]];
     const rows = allStudents.map((student, index) => [
       String(index + 1),
       String(student.course?.name || 'N/A'),
       String(student.name || ''),
       String(student.prn || ''),
       student.prn ? `${student.prn}@slms.ssodl.edu.in` : '',
       String(student.email || ''),
       String(student.phone || '')
     ]);
    
     const data = [...headers, ...rows];


     // Create workbook and worksheet using aoa_to_sheet (array of arrays)
     const ws = XLSX.utils.aoa_to_sheet(data);
     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, 'Students');


     // Set column widths
     ws['!cols'] = [
       { wch: 8 },   // Sr No
       { wch: 25 },  // Programme
       { wch: 35 },  // Name
       { wch: 25 },  // PRN
       { wch: 35 },  // Institutional Email
       { wch: 30 },  // Email
       { wch: 20 }   // Mobile Number
     ];


     // Generate Excel file (binary for compatibility with large datasets)
     const fileName = `students_all_${new Date().toISOString().split('T')[0]}.xlsx`;
     const wbout = XLSX.write(wb, {
       bookType: 'xlsx',
       type: 'binary',
       bookSST: false,
       compression: true
     });


     const s2ab = (s) => {
       const buf = new ArrayBuffer(s.length);
       const view = new Uint8Array(buf);
       for (let i = 0; i < s.length; i += 1) {
         view[i] = s.charCodeAt(i) & 0xff;
       }
       return buf;
     };


     const blob = new Blob([s2ab(wbout)], {
       type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
     });
     saveAs(blob, fileName);
    
     showSnackbar(`Export successful! ${allStudents.length} students exported.`, 'success');
     setExportLoading(false);
   } catch (error) {
     console.error('Error exporting to Excel:', error);
     showSnackbar('Failed to export: ' + (error.response?.data?.message || error.message), 'error');
     setExportLoading(false);
   }
 };


 const handleSyncFilterChange = (event) => {
   setSyncFilter(event.target.value);
   setLoading(true);
   // Reset to first page when filtering
   setPaginationModel(prev => ({ ...prev, page: 0 }));
   fetchStudents();
 };


 const columns = [
   { field: 'prn', headerName: 'PRN', width: 130 },
   { field: 'name', headerName: 'Name', width: 200 },
   { field: 'email', headerName: 'Email', width: 200 },
   { field: 'phone', headerName: 'Phone', width: 130 },
   { field: 'course', headerName: 'Course', width: 150 },
   {
     field: 'sync',
     headerName: 'Sync Status',
     width: 120,
     renderCell: (params) => (
       <Box>
         <Chip
           label={params.row.sync ? 'Synced' : 'Not Synced'}
           color={params.row.sync ? 'success' : 'warning'}
           size="small"
         />
       </Box>
     )
   },
   {
     field: 'status',
     headerName: 'Status',
     width: 120,
     renderCell: (params) => (
       <Box>
         <Chip
           label={params.row.isDeleted ? 'Deleted' : (params.row.isActive ? 'Active' : 'Inactive')}
           color={params.row.isDeleted ? 'error' : (params.row.isActive ? 'success' : 'default')}
           size="small"
         />
       </Box>
     )
   },
   {
     field: 'actions',
     headerName: 'Actions',
     width: 200,
     sortable: false,
     renderCell: (params) => (
       <Box>
         {params.row.isDeleted ? (
           // Actions for deleted students
           <>
             <Tooltip title="Restore Student">
               <IconButton
                 size="small"
                 onClick={() => handleRestore(params.row.prn)}
                 color="success"
               >
                 <PersonAddIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Permanently Delete">
               <IconButton
                 size="small"
                 onClick={() => handleDelete(params.row.prn, params?.row?.isDeleted)}
                 color="error"
               >
                 <DeleteIcon />
               </IconButton>
             </Tooltip>
           </>
         ) : (
           // Actions for active students
           <>
             <Tooltip title="Edit Student">
               <IconButton
                 size="small"
                 onClick={() => handleEdit(params.row)}
                 color="primary"
               >
                 <EditIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Delete Student">
               <IconButton
                 size="small"
                 onClick={() => handleDelete(params.row.prn, params?.row?.isDeleted)}
                 color="error"
               >
                 <DeleteIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Reset Password">
               <IconButton
                 size="small"
                 onClick={() => handleResetPassword(params.row)}
                 color="info"
               >
                 <VpnKeyIcon />
               </IconButton>
             </Tooltip>
             <Tooltip title="Toggle Status">
               <IconButton
                 size="small"
                 onClick={() => handleToggleStatus(params.row)}
                 color={params.row.isActive ? 'warning' : 'success'}
               >
                 <PowerSettingsNewIcon />
               </IconButton>
             </Tooltip>
           </>
         )}
       </Box>
     )
   }
 ];


 const rows = students.map(student => ({
   id: student._id,
   prn: student.prn,
   name: student.name,
   email: student.email,
   phone: student.phone,
   course: student.course?.name || 'N/A',
   isActive: student.isActive,
   isDeleted: student.isDeleted,
   sync: student.sync,
 }));


 if (loading) {
   return (
     <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
       <CircularProgress />
     </Box>
   );
 }


 return (
   <Box>
     <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
       <Typography variant="h4">
         Student Management
       </Typography>
     </Box>


     <Paper sx={{ p: 3, mb: 3 }}>
       <form onSubmit={handleSearch}>
         <Grid container spacing={2} alignItems="center">
           <Grid item xs={12} md={4}>
             <TextField
               fullWidth
               label="Search by PRN, Name, Email, or Phone"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               InputProps={{
                 startAdornment: (
                   <InputAdornment position="start">
                     <SearchIcon />
                   </InputAdornment>
                 ),
               }}
             />
           </Grid>
           <Grid item xs={6} md={1}>
             <Button
               type="submit"
               variant="contained"
               size="small"
               fullWidth
               startIcon={<SearchIcon />}
               disabled={loading}
             >
               Search
             </Button>
           </Grid>
           <Grid item xs={6} md={1}>
             <Button
               variant="outlined"
               size="small"
               fullWidth
               startIcon={<CloseIcon />}
               onClick={() => {
                 setSearchTerm('');
                 fetchStudents();
               }}
             >
               Clear
             </Button>
           </Grid>
           <Grid item xs={12} md={1}>
             <FormControl fullWidth size="small">
               <InputLabel>Sync Status</InputLabel>
               <Select
                 value={syncFilter}
                 label="Sync Status"
                 onChange={handleSyncFilterChange}
               >
                 <MenuItem value="">All</MenuItem>
                 <MenuItem value="true">Synced</MenuItem>
                 <MenuItem value="false">Not Synced</MenuItem>
               </Select>
             </FormControl>
           </Grid>
           <Grid item xs={12} md={2}>
             <Tooltip title="Upload students from a CSV file">
               <Button
                 variant="contained"
                 color="success"
                 component="label"
                 size="small"
                 fullWidth
                 startIcon={uploadLoading ? <Box sx={{ width: 16, height: 16 }}><LinearProgress /></Box> : <CloudUploadIcon />}
                 disabled={uploadLoading}
               >
                 {uploadLoading ? 'Uploading...' : 'Upload Student'}
                 <input
                   type="file"
                   hidden
                   accept=".xlsx,.xlsm,.ods,.csv,.xla,.numbers,.xls"
                   onChange={handleFileUpload}
                   disabled={uploadLoading}
                 />
               </Button>
             </Tooltip>
           </Grid>
           <Grid item xs={12} md={2}>
             <Button
               variant="contained"
               color="primary"
               size="small"
               fullWidth
               startIcon={<PersonAddIcon />}
               onClick={() => setAddDialogOpen(true)}
             >
               Add Student
             </Button>
           </Grid>
           <Grid item xs={12} md={1}>
             <Button
               variant="contained"
               color="secondary"
               size="small"
               fullWidth
               startIcon={bulkSyncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
               onClick={handleBulkSync}
               disabled={bulkSyncLoading}
             >
               {bulkSyncLoading ? 'Syncing...' : 'Sync All'}
             </Button>
           </Grid>
           <Grid item xs={6} md={1}>
             <Tooltip title="Export all students to Excel">
               <Button
                 variant="contained"
                 color="success"
                 size="small"
                 fullWidth
                 startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                 onClick={handleExportExcel}
                 disabled={exportLoading}
               >
                 {exportLoading ? 'Exporting...' : 'Excel'}
               </Button>
             </Tooltip>
           </Grid>
         </Grid>
         {uploadLoading && <LinearProgress sx={{ mt: 2 }} />}
       </form>
     </Paper>


     <Paper sx={{ height: 600, width: '100%' }}>
       <DataGrid
         rows={rows}
         columns={columns}
         pagination
         paginationMode="server"
         rowCount={totalStudents}
         paginationModel={paginationModel}
         onPaginationModelChange={handlePaginationModelChange}
         pageSizeOptions={[10, 25, 50, 100]}
         checkboxSelection
         disableSelectionOnClick
         loading={loading}
         getRowId={(row) => row.id}
       />
     </Paper>
    
     {/* Add Student Dialog */}
     <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
       <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         Add New Student
         <IconButton onClick={handleCloseAddDialog} size="small">
           <CloseIcon />
         </IconButton>
       </DialogTitle>
       <DialogContent>
         <Grid container spacing={2} sx={{ mt: 1 }}>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="PRN"
               value={newStudent.prn}
               onChange={(e) => setNewStudent({ ...newStudent, prn: e.target.value })}
               error={!!errors.prn}
               helperText={errors.prn}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Full Name"
               value={newStudent.name}
               onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
               error={!!errors.name}
               helperText={errors.name}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Email"
               type="email"
               value={newStudent.email}
               onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
               error={!!errors.email}
               helperText={errors.email}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Phone"
               value={newStudent.phone}
               onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
               error={!!errors.phone}
               helperText={errors.phone}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <FormControl fullWidth error={!!errors.courseName} required>
               <InputLabel>Course Name</InputLabel>
               <Select
                 value={newStudent.courseName}
                 label="Course Name"
                 onChange={(e) => setNewStudent({ ...newStudent, courseName: e.target.value })}
               >
                 {courses.map((course) => (
                   <MenuItem key={course._id} value={course.name}>
                     {course.name}
                   </MenuItem>
                 ))}
               </Select>
               {errors.courseName && (
                 <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                   {errors.courseName}
                 </Typography>
               )}
             </FormControl>
           </Grid>
         </Grid>
       </DialogContent>
       <DialogActions sx={{ px: 3, pb: 2 }}>
         <Button onClick={handleCloseAddDialog} color="inherit">
           Cancel
         </Button>
         <Button
           onClick={handleAddStudent}
           variant="contained"
           disabled={addStudentLoading}
           startIcon={addStudentLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
         >
           {addStudentLoading ? 'Adding...' : 'Add Student'}
         </Button>
       </DialogActions>
     </Dialog>


     {/* Edit Student Dialog */}
     <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
       <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         Edit Student
         <IconButton onClick={handleCloseEditDialog} size="small">
           <CloseIcon />
         </IconButton>
       </DialogTitle>
       <DialogContent>
         <Grid container spacing={2} sx={{ mt: 1 }}>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="PRN"
               value={newStudent.prn}
               onChange={(e) => setNewStudent({ ...newStudent, prn: e.target.value })}
               error={!!errors.prn}
               helperText={errors.prn}
               required
               disabled
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Full Name"
               value={newStudent.name}
               onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
               error={!!errors.name}
               helperText={errors.name}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Email"
               type="email"
               value={newStudent.email}
               onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
               error={!!errors.email}
               helperText={errors.email}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="Phone"
               value={newStudent.phone}
               onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
               error={!!errors.phone}
               helperText={errors.phone}
               required
             />
           </Grid>
           <Grid item xs={12}>
             <FormControl fullWidth error={!!errors.courseName} required>
               <InputLabel>Course Name</InputLabel>
               <Select
                 value={newStudent.courseName}
                 label="Course Name"
                 onChange={(e) => setNewStudent({ ...newStudent, courseName: e.target.value })}
               >
                 {courses.map((course) => (
                   <MenuItem key={course._id} value={course.name}>
                     {course.name}
                   </MenuItem>
                 ))}
               </Select>
               {errors.courseName && (
                 <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                   {errors.courseName}
                 </Typography>
               )}
             </FormControl>
           </Grid>
         </Grid>
       </DialogContent>
       <DialogActions sx={{ px: 3, pb: 2 }}>
         <Button onClick={handleCloseEditDialog} color="inherit">
           Cancel
         </Button>
         <Button
           onClick={handleUpdateStudent}
           variant="contained"
           disabled={editStudentLoading}
           startIcon={editStudentLoading ? <CircularProgress size={20} /> : <EditIcon />}
         >
           {editStudentLoading ? 'Updating...' : 'Update Student'}
         </Button>
       </DialogActions>
     </Dialog>


     {/* Reset Password Dialog */}
     <Dialog open={resetPasswordDialogOpen} onClose={handleCloseResetPasswordDialog} maxWidth="sm" fullWidth>
       <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         Reset Password for {selectedStudentForReset?.name}
         <IconButton onClick={handleCloseResetPasswordDialog} size="small">
           <CloseIcon />
         </IconButton>
       </DialogTitle>
       <DialogContent>
         <Grid container spacing={2} sx={{ mt: 1 }}>
           <Grid item xs={12}>
             <TextField
               fullWidth
               label="New Password"
               type="text"
               value={resetPassword}
               onChange={(e) => setResetPassword(e.target.value)}
               helperText="Password must be at least 6 characters long"
               required
             />
           </Grid>
         </Grid>
       </DialogContent>
       <DialogActions sx={{ px: 3, pb: 2 }}>
         <Button onClick={handleCloseResetPasswordDialog} color="inherit">
           Cancel
         </Button>
         <Button
           onClick={handleSendResetPassword}
           variant="contained"
           color="primary"
           startIcon={<VpnKeyIcon />}
         >
           Send
         </Button>
       </DialogActions>
     </Dialog>


     <Snackbar
       open={snackbarOpen}
       autoHideDuration={6000}
       onClose={() => setSnackbarOpen(false)}
       anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
     >
       <Alert
         onClose={() => setSnackbarOpen(false)}
         severity={snackbarSeverity}
         sx={{ width: '100%' }}
       >
         {snackbarMessage}
       </Alert>
     </Snackbar>
   </Box>
 );
};


export default StudentManagement;